import React, { useEffect } from 'react';
import { Modal, Button, ProgressBar, Spinner, Alert } from 'react-bootstrap';
import { IconCheck, IconAlertTriangle, IconPointFilled } from '@tabler/icons-react';
import { usePropertyActivationStatus } from '../../../services/properties/hooks/usePropertyActivationStatus';
import { ActivationStep } from '../../../services/properties/response/PropertyActivationResponse';

interface ActivationProgressModalProps {
    show: boolean;
    propertyId: string | null;
    propertyName?: string;
    activationId: string | null;
    direction: 'activate' | 'deactivate' | null;
    onClose: () => void;
    onFinished: (status: 'completed' | 'completed_with_errors' | 'failed') => void;
    /** Called when the user clicks Retry — parent re-fires the same toggle mutation. */
    onRetry: () => void;
}

const TERMINAL_STATUSES = ['completed', 'completed_with_errors', 'failed'];

/**
 * Shows live progress for a property activate/deactivate job: each step
 * (Activating property / Sync Room Type / Sync Rateplan / Sync Link Rate for
 * activate; 2 steps for deactivate) as reported by the backend, polled via
 * `usePropertyActivationStatus`. Steps are REAL progress from the backend
 * (one Channex call = one processed count), not a cosmetic timer — the
 * backend runs one Channex call at a time specifically so this modal can
 * show truthful step-by-step state.
 */
const ActivationProgressModal: React.FC<ActivationProgressModalProps> = ({
    show,
    propertyId,
    propertyName,
    activationId,
    direction,
    onClose,
    onFinished,
    onRetry,
}) => {
    const { data } = usePropertyActivationStatus(propertyId, activationId, {
        enabled: show && !!activationId,
    });

    const status = data?.data;
    const isTerminal = !!status && TERMINAL_STATUSES.includes(status.status);
    const isRunning = show && !!activationId && !isTerminal;

    useEffect(() => {
        if (status && TERMINAL_STATUSES.includes(status.status)) {
            onFinished(status.status as 'completed' | 'completed_with_errors' | 'failed');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status?.status]);

    const overallPercent = status && status.total > 0
        ? Math.round((status.processed / status.total) * 100)
        : 0;

    const title = direction === 'deactivate' ? 'Deactivating Property' : 'Activating Property';

    return (
        <Modal show={show} onHide={onClose} centered backdrop="static" keyboard={!isRunning}>
            <Modal.Header closeButton={!isRunning}>
                <Modal.Title>{title}{propertyName ? `: ${propertyName}` : ''}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <ProgressBar
                    now={overallPercent}
                    animated={isRunning}
                    striped={isRunning}
                    variant={status?.status === 'failed' ? 'danger' : (status?.failed ?? 0) > 0 ? 'warning' : 'primary'}
                    style={{ height: 8 }}
                    className="mb-3"
                />

                <ul className="list-unstyled mb-0">
                    {(status?.steps ?? []).map((step) => (
                        <StepRow key={step.key} step={step} />
                    ))}
                    {!status && (
                        <li className="d-flex align-items-center gap-2 text-muted">
                            <Spinner animation="border" size="sm" />
                            Starting…
                        </li>
                    )}
                </ul>

                {status && status.errors.length > 0 && (
                    <Alert variant="warning" className="mt-3 mb-0">
                        <div className="fw-semibold mb-1">
                            {status.errors.length} item{status.errors.length > 1 ? 's' : ''} could not be synced:
                        </div>
                        <ul className="mb-0 ps-3 small">
                            {status.errors.map((err, idx) => (
                                <li key={idx}>
                                    <strong>{err.name ?? err.type}</strong>: {err.message}
                                </li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {status?.error_message && (
                    <Alert variant="danger" className="mt-3 mb-0">
                        {status.error_message}
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                {isTerminal && status?.status !== 'completed' && (
                    <Button variant="warning" onClick={onRetry}>
                        Retry
                    </Button>
                )}
                <Button variant="secondary" onClick={onClose} disabled={isRunning}>
                    {isTerminal ? 'Close' : 'Please wait…'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const StepRow: React.FC<{ step: ActivationStep }> = ({ step }) => {
    const icon = (() => {
        switch (step.status) {
            case 'done':
                return <IconCheck size={16} className="text-success" />;
            case 'done_with_errors':
                return <IconAlertTriangle size={16} className="text-warning" />;
            case 'skipped':
                return <IconPointFilled size={16} className="text-muted" />;
            case 'running':
                return <Spinner animation="border" size="sm" />;
            default:
                return <IconPointFilled size={16} className="text-muted opacity-50" />;
        }
    })();

    return (
        <li className="d-flex align-items-center justify-content-between gap-2 py-1">
            <span className="d-flex align-items-center gap-2">
                {icon}
                <span className={step.status === 'pending' ? 'text-muted' : undefined}>{step.label}</span>
            </span>
            {step.total > 1 && (
                <span className="text-muted small">{step.processed}/{step.total}</span>
            )}
        </li>
    );
};

export default ActivationProgressModal;
